import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, query, where, getDocs, onSnapshot, Timestamp } from 'firebase/firestore';
import { getPublishedBlogs } from '../../services/blogService';

const StatCard = ({ icon, label, value, description }) => (
  <div className="col-md-6 col-lg-3 mb-4">
    <div className="card shadow-sm border-0 rounded-3 h-100">
      <div className="card-body">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div>
            <p className="text-muted small mb-1">{label}</p>
            <h4 className="mb-0">{value}</h4>
          </div>
          <div className="fs-1 text-primary opacity-50">
            <i className={`bi ${icon}`}></i>
          </div>
        </div>
        <small className="text-muted">{description}</small>
      </div>
    </div>
  </div>
);

const AdminAnalytics = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('7');
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalPageViews: 0,
    blogViews: 0,
    whatsappClicks: 0,
    topPages: [],
    topBlogs: [],
  });
  const [allBlogs, setAllBlogs] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Fetch blogs for title mapping
  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const { blogs } = await getPublishedBlogs(100);
        setAllBlogs(blogs);
      } catch (err) {
        console.error('Error loading blogs:', err);
      }
    };
    fetchBlogs();
  }, []);

  // Subscribe to real-time analytics events
  useEffect(() => {
    setLoading(true);
    
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - parseInt(dateRange));
    startDate.setHours(0, 0, 0, 0);

    const q = query(
      collection(db, 'analytics_events'),
      where('timestamp', '>=', Timestamp.fromDate(startDate))
    );

    // Real-time listener
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const eventsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          timestamp: doc.data().timestamp?.toDate?.() || new Date(doc.data().timestamp),
        }));

        setEvents(eventsData);
        setLastUpdated(new Date());
        calculateStats(eventsData);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching analytics:', err);
        setError('Unable to load analytics data.');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [dateRange]);

  const calculateStats = (eventsData) => {
    // Unique users
    const uniqueUsers = new Set(eventsData.map(e => e.userId)).size;

    // Page views (excluding admin routes)
    const pageViews = eventsData.filter(
      e => e.eventType === 'page_view' && !e.page?.startsWith('/admin')
    ).length;

    // Blog views
    const blogViews = eventsData.filter(e => e.eventType === 'blog_view').length;

    // WhatsApp clicks
    const whatsappClicks = eventsData.filter(e => e.eventType === 'whatsapp_click').length;

    // Top pages
    const pageMap = {};
    eventsData
      .filter(e => e.eventType === 'page_view' && !e.page?.startsWith('/admin'))
      .forEach(e => {
        pageMap[e.page] = (pageMap[e.page] || 0) + 1;
      });

    const topPages = Object.entries(pageMap)
      .map(([page, count]) => ({ page, views: count }))
      .sort((a, b) => b.views - a.views)
      .slice(0, 10);

    // Top blogs
    const blogMap = {};
    eventsData.filter(e => e.eventType === 'blog_view').forEach(e => {
      blogMap[e.blogSlug] = (blogMap[e.blogSlug] || 0) + 1;
    });

    const topBlogs = Object.entries(blogMap)
      .map(([slug, count]) => {
        const blog = allBlogs.find(b => b.slug === slug);
        return {
          title: blog?.title || slug,
          slug,
          views: count,
        };
      })
      .sort((a, b) => b.views - a.views)
      .slice(0, 10);

    setStats({
      totalUsers: uniqueUsers,
      totalPageViews: pageViews,
      blogViews,
      whatsappClicks,
      topPages,
      topBlogs,
    });
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    // The onSnapshot listener will handle the refresh automatically
    setTimeout(() => setIsRefreshing(false), 1000);
  };

  const handleDateRangeChange = (e) => {
    setDateRange(e.target.value);
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading analytics...</span>
        </div>
      </div>
    );
  }

  const totalViews = stats.topPages.reduce((sum, page) => sum + page.views, 0) || 1;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">Analytics Dashboard</h2>
          <p className="text-muted mb-0">Real-time event tracking from your website</p>
        </div>
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <i className="bi bi-arrow-clockwise me-2"></i>
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </button>
          <select
            className="form-select"
            style={{ width: '200px' }}
            value={dateRange}
            onChange={handleDateRangeChange}
          >
            <option value="7">Last 7 Days</option>
            <option value="30">Last 30 Days</option>
            <option value="90">Last 90 Days</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="alert alert-warning alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)}></button>
        </div>
      )}

      {events.length === 0 ? (
        <div className="alert alert-info">
          <i className="bi bi-info-circle me-2"></i>
          No analytics data available yet. Start navigating your site to see real-time data.
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="row">
            <StatCard icon="bi-people" label="Total Users" value={stats.totalUsers} description="Unique visitors" />
            <StatCard icon="bi-eye" label="Page Views" value={stats.totalPageViews} description="Total page visits" />
            <StatCard icon="bi-book" label="Blog Views" value={stats.blogViews} description="Blog post views" />
            <StatCard icon="bi-chat-left" label="WhatsApp Clicks" value={stats.whatsappClicks} description="WhatsApp interactions" />
          </div>

          {/* Top Pages */}
          {stats.topPages.length > 0 && (
            <div className="card shadow-sm border-0 rounded-3 mb-4">
              <div className="card-header bg-white border-0">
                <h5 className="mb-0">Top Pages</h5>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: '50px' }}>Rank</th>
                        <th>Page</th>
                        <th style={{ width: '120px' }}>Views</th>
                        <th style={{ width: '120px' }}>% of Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.topPages.map((page, idx) => (
                        <tr key={idx}>
                          <td>
                            <span className="badge bg-primary rounded-pill">{idx + 1}</span>
                          </td>
                          <td>
                            <code className="text-dark">{page.page}</code>
                          </td>
                          <td className="fw-semibold">{page.views}</td>
                          <td>
                            <span className="badge bg-light text-dark">{((page.views / totalViews) * 100).toFixed(1)}%</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Top Blog Posts */}
          {stats.topBlogs.length > 0 && (
            <div className="card shadow-sm border-0 rounded-3 mb-4">
              <div className="card-header bg-white border-0">
                <h5 className="mb-0">Top Blog Posts</h5>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: '50px' }}>Rank</th>
                        <th>Blog Title</th>
                        <th style={{ width: '120px' }}>Views</th>
                        <th style={{ width: '120px' }}>% of Blogs</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.topBlogs.map((blog, idx) => (
                        <tr key={idx}>
                          <td>
                            <span className="badge bg-success rounded-pill">{idx + 1}</span>
                          </td>
                          <td>{blog.title}</td>
                          <td className="fw-semibold">{blog.views}</td>
                          <td>
                            <span className="badge bg-light text-dark">
                              {stats.blogViews > 0 ? ((blog.views / stats.blogViews) * 100).toFixed(1) : 0}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Recent Events */}
          <div className="card shadow-sm border-0 rounded-3 mb-4">
            <div className="card-header bg-white border-0">
              <h5 className="mb-0">Recent Events</h5>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead>
                    <tr>
                      <th>Event Type</th>
                      <th>Details</th>
                      <th>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.slice(0, 20).reverse().map((event) => (
                      <tr key={event.id}>
                        <td>
                          <span className="badge bg-info">
                            {event.eventType === 'page_view' && 'Page View'}
                            {event.eventType === 'blog_view' && 'Blog View'}
                            {event.eventType === 'whatsapp_click' && 'WhatsApp Click'}
                          </span>
                        </td>
                        <td>
                          {event.eventType === 'page_view' && <code>{event.page}</code>}
                          {event.eventType === 'blog_view' && <code>{event.blogTitle}</code>}
                          {event.eventType === 'whatsapp_click' && <span>From {event.source}</span>}
                        </td>
                        <td>
                          <small className="text-muted">
                            {event.timestamp?.toLocaleTimeString?.()}
                          </small>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Info Section */}
          <div className="alert alert-light border">
            <h6 className="mb-2"><i className="bi bi-info-circle me-2"></i>About This Data</h6>
            <p className="mb-1 small text-muted">
              This analytics dashboard displays real-time event tracking data directly from your website visitors.
            </p>
            <p className="mb-0 small text-muted">
              <strong>Last Updated:</strong> {lastUpdated?.toLocaleTimeString()}
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminAnalytics;
